package com.techspiration.webuserapp.home

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import com.techspiration.core.contract.PublicListingStatsResponse
import com.techspiration.webuserapp.api.PublicApi
import com.techspiration.webuserapp.site.SectionContainer
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Sayaç bölümünün üç durumu — "veri yok" ile "sıfır ilan var" ASLA karıştırılmaz. */
private sealed interface StatsState {
    data object Loading : StatsState
    data class Ready(val stats: PublicListingStatsResponse) : StatsState
    data object Failed : StatsState
}

/**
 * Sayı bandı — başlık/açıklamanın altında canlı ilan sayaçları, hemen altında üç **temsilî**
 * ilan kartı ([SampleListingsSection]). İkisi tek bölümde tutulur: sayı soyut, kart somut;
 * yan yana durunca "şu kadar ilan var, böyle görünüyorlar" mesajı tek okumada kuruluyor.
 *
 * Sayılar `GET /api/v1/public/listing-stats` ucundan gelir (auth yok, 60 sn cache'lenebilir).
 *
 * **Yükleniyor ve hata durumunda "0" GÖSTERİLMEZ**, yer tutucu (`—`) basılır: sıfır geçerli bir
 * cevaptır ("bugün hiç ilan yok") ve onu bilinmezlik için kullanmak kullanıcıya yanlış bilgi verir.
 * Hata durumunda ayrıca kısa bir açıklama satırı gösterilir; bölüm gizlenmez, çünkü sessizce
 * kaybolan bir blok sayfada açıklanamayan bir boşluk bırakır.
 */
@Composable
fun ListingStatsSection(api: PublicApi = remember { PublicApi() }) {
    val state by produceState<StatsState>(initialValue = StatsState.Loading, api) {
        value = runCatching { api.listingStats() }
            .fold(onSuccess = { StatsState.Ready(it) }, onFailure = { StatsState.Failed })
    }

    SectionContainer(id = "ilanlar", extraClass = "section-tight") {
        H2(attrs = { classes("t-h2") }) { Text("Şu anda yayında olan ilanlar") }
        Div(attrs = { classes("stats-grid") }) {
            StatCell("Tüm İlanlar", state.valueOf { it.total })
            StatCell("Kamu İlanları", state.valueOf { it.kurumIci })
            StatCell("Kamu Dışı İlanlar", state.valueOf { it.kurumDisi })
        }
        if (state is StatsState.Failed) {
            P(attrs = { classes("t-label", "stats-note") }) {
                Text("İlan sayıları şu anda alınamadı. Güncel listeyi uygulamadan görebilirsin.")
            }
        }
        SampleListingsSection()
    }
}

/**
 * Duruma göre gösterilecek metin: hazırsa biçimlenmiş sayı, değilse yer tutucu.
 * Yükleniyor/hata ayrımı metinde değil, [StatCell]'in `aria-busy`/açıklama satırında verilir.
 */
private inline fun StatsState.valueOf(pick: (PublicListingStatsResponse) -> Int): String =
    when (this) {
        is StatsState.Ready -> formatCount(pick(stats))
        StatsState.Loading, StatsState.Failed -> PLACEHOLDER
    }

/** Sayı bilinmiyorken basılan yer tutucu — "0" DEĞİL (bkz. [ListingStatsSection]). */
private const val PLACEHOLDER = "—"

/** Türkçe binlik ayracı (nokta): 1240 → "1.240". `toLocaleString` yerine sabit ve test edilebilir. */
private fun formatCount(value: Int): String {
    val digits = value.toString()
    return buildString {
        digits.forEachIndexed { index, char ->
            if (index > 0 && (digits.length - index) % 3 == 0) append('.')
            append(char)
        }
    }
}

@Composable
private fun StatCell(label: String, value: String) {
    Div(attrs = { classes("stat") }) {
        Span(
            attrs = {
                classes("stat-value")
                if (value == PLACEHOLDER) attr("aria-busy", "true")
            },
        ) { Text(value) }
        Span(attrs = { classes("t-label") }) { Text(label) }
    }
}

/**
 * Üç **statik** örnek ilan kartı — veri [SAMPLE_LISTINGS]'ten gelir, backend'e gitmez.
 * Kartlar bilinçli olarak tıklanamaz (link/buton yok) ve "Örnek" rozeti taşır.
 */
@Composable
private fun SampleListingsSection() {
    Div(attrs = { classes("card-grid", "sample-grid") }) {
        SAMPLE_LISTINGS.forEach { listing ->
            Div(attrs = { classes("card", "sample-card") }) {
                Div(attrs = { classes("sample-head") }) {
                    Span(attrs = { classes("t-label") }) { Text(listing.institution) }
                    Span(attrs = { classes("sample-badge") }) { Text("Örnek") }
                }
                H3(attrs = { classes("t-title") }) { Text(listing.title) }
                Div(attrs = { classes("sample-meta") }) {
                    SampleMeta("📍", listing.city)
                    SampleMeta("🎓", listing.educationLevel)
                    SampleMeta("🗓", "Son başvuru ${listing.deadline}")
                }
            }
        }
    }
    P(attrs = { classes("t-label", "sample-note") }) {
        Text("Yukarıdaki kartlar temsilîdir; gerçek ilanlar uygulamada listelenir.")
    }
}

@Composable
private fun SampleMeta(icon: String, text: String) {
    Span(attrs = { classes("sample-meta-item") }) {
        Span(attrs = { attr("aria-hidden", "true") }) { Text(icon) }
        Text(text)
    }
}
